import { useEffect, useState } from "react";

import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import Close from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Card,
  IconButton,
  Modal,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";

import DashboardDemoVideo from "@app/analytics/components/DashboardDemoVideo";
import PlayVideoButton from "@app/analytics/components/PlayVideoButton";
import VideoModal from "@app/analytics/components/VideoModal";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import usePopups from "@app/analytics/hooks/usePopups";
import * as Events from "@app/analytics/tracking/events";
import useAuth from "@app/common/auth/useAuth";
import Stepper from "@app/common/components/Stepper";
import useApps from "@app/common/hooks/useApps";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import usePreloadImages from "@app/common/hooks/usePreloadImages";
import ROUTES from "@app/common/routes";
import { pluralize, withQueryParams } from "@app/common/utils";
import * as UserUtils from "@app/common/utils/userUtils";

import LoadSpinner from "@rui/LoadSpinner";

import ConnectCalendar from "./ConnectCalendar";
import getTutorialStepConfigs from "./getTutorialStepConfigs";
import TUTORIAL_IMAGES from "../assets";

const BUTTON_STYLE: SxProps = {
  py: "8px",
  height: "46px",
  bgcolor: "shades.primary.500",
  color: "#FFF",
  px: "12px",
  minWidth: "155px",
  "&:hover": {
    color: "#FFF",
    bgcolor: "shades.primary.600",
  },
};

const FirstTimeTutorial = ({ priority = 100 }: { priority?: number }) => {
  usePreloadImages(TUTORIAL_IMAGES);

  const [queryParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useAuth();
  const { client } = useApps();
  const { updateUserMetadata } = useUserSettings();
  const {
    hasConnectedCalendarProviders,
    isLoading: isCalendarProvidersLoading,
  } = useConnectedProviders();
  const { isTrial, scheduledPlan } = useBillingContext();
  const { canConnectCalendars } = useFeatureAccess();

  const calendarConnectSuccess = queryParams.get("success");
  const calendarConnectError = queryParams.get("error");

  const { show, reset } = usePopups({
    id: "meetingManagerTutorial",
    priority,
    shouldShow: true,
  });

  const [activeStep, setActiveStep] = useState<number>(0);
  const [showDemoVideo, setShowDemoVideo] = useState<boolean>(false);
  const [skipCalendarConnect, setSkipCalendarConnect] =
    useState<boolean>(false);

  const steps = getTutorialStepConfigs({
    plan: scheduledPlan?.type ?? "free",
    isTrial,
    client,
  });

  const stepsRemaining = steps.length - activeStep - 1;
  const activeStepConfig = steps[activeStep];
  const onLastStep = activeStep === steps.length - 1;

  const showFreeTrialFooter =
    steps[steps.length - 1].id === "free_trial_started" &&
    activeStepConfig.id !== "free_trial_started";

  const showCalendarConnectStep =
    canConnectCalendars &&
    !hasConnectedCalendarProviders &&
    !skipCalendarConnect;
  const showPreviewMeetingReports = isTrial;

  useEffect(() => {
    if (!show) return;

    if (calendarConnectSuccess || !showCalendarConnectStep) {
      setSkipCalendarConnect(true);
    }

    Events.tutorialBegin({ tutorial: "meeting_manager" });
  }, [show, calendarConnectSuccess, showCalendarConnectStep]);

  if (!show) return null;

  const handleGoToStep = (step: number) => {
    setActiveStep(step);
  };

  const handleNext = () => {
    if (!skipCalendarConnect) {
      return setSkipCalendarConnect(true);
    }

    setActiveStep((currentStep) => currentStep + 1);
  };

  const handleFinish = () => {
    completeTutorial(
      showPreviewMeetingReports ? ROUTES.GO.SAMPLE_REPORT : undefined
    );
  };

  const handleCloseCalendarConnect = () => setSkipCalendarConnect(true);

  const completeTutorial = (route?: string) => {
    reset();
    Events.tutorialEnd({ tutorial: "meeting_manager", step: activeStep });
    updateUserMetadata.mutate({ meetingManagerTutorialComplete: true });

    const showSettingsConfirmPopup =
      UserUtils.requireSettingsConfirmation(user);

    if (route || showSettingsConfirmPopup) {
      navigate(
        withQueryParams(route ?? location.pathname, {
          popup_id: showSettingsConfirmPopup ? "settings" : undefined,
        })
      );
    }
  };

  const handleDismiss = () => completeTutorial();

  const handleDemoVideoOpen = () => setShowDemoVideo(true);
  const handleDemoVideoClose = () => setShowDemoVideo(false);

  const screenHasVideo = activeStepConfig?.isVideo;
  const CustomContent = activeStepConfig?.customContent;

  return (
    <>
      {showDemoVideo && (
        <VideoModal open={showDemoVideo} onClose={handleDemoVideoClose}>
          <DashboardDemoVideo />
        </VideoModal>
      )}
      <Modal
        open={show}
        onClose={
          skipCalendarConnect ? handleDismiss : handleCloseCalendarConnect
        }
        disableAutoFocus
      >
        <Card
          data-test-id={`tutorial-card-${activeStepConfig.id}`}
          sx={{
            position: "absolute",
            width: "398px",
            height: "610px",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: "100%",
            maxWidth: "100%",
            overflowY: "scroll",
            p: 0,
          }}
        >
          {isCalendarProvidersLoading ? (
            <Stack
              justifyContent="center"
              alignItems="center"
              sx={{ height: "100%" }}
            >
              <LoadSpinner />
            </Stack>
          ) : (
            <Stack sx={{ height: "100%" }}>
              {showCalendarConnectStep ? (
                <>
                  <IconButton
                    data-test-id="close-tutorial-button"
                    sx={{
                      position: "absolute",
                      right: 6,
                      top: 6,
                      bgcolor: "#FFFFFF80",
                    }}
                    onClick={handleCloseCalendarConnect}
                  >
                    <Close sx={{ fill: "#282828" }} />
                  </IconButton>
                  <ConnectCalendar
                    handleNextStep={handleNext}
                    returnTo={`${location.pathname}?mm_tut_cont=true`}
                    calendarError={Boolean(calendarConnectError)}
                  />
                </>
              ) : (
                <>
                  {activeStepConfig?.image && (
                    <Box
                      sx={{
                        bgcolor: "#F1F4FF",
                        height: "200px",
                        borderRadius: "12px",
                        position: "relative",
                      }}
                    >
                      <IconButton
                        data-test-id="close-tutorial-button"
                        sx={{
                          position: "absolute",
                          right: 6,
                          top: 6,
                          bgcolor: "#FFFFFF80",
                        }}
                        onClick={handleDismiss}
                      >
                        <Close sx={{ fill: "#282828" }} />
                      </IconButton>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        spacing={3}
                      >
                        <img height="200px" src={activeStepConfig.image} />
                      </Stack>

                      {screenHasVideo && (
                        <PlayVideoButton onClick={handleDemoVideoOpen} />
                      )}
                    </Box>
                  )}
                  <Stack
                    sx={{ px: "24px", flexGrow: 1 }}
                    justifyContent="space-between"
                  >
                    <Stack spacing={1}>
                      <>
                        <Typography
                          variant="h2"
                          sx={{ pt: "16px", fontSize: "1.3125rem" }}
                        >
                          {activeStepConfig?.title}
                        </Typography>
                        {CustomContent && <CustomContent />}
                        {activeStepConfig?.content && (
                          <Stack spacing={2}>
                            {activeStepConfig.content.map((paragraph, i) => (
                              <Typography
                                key={i}
                                variant="bodyPrimaryReg"
                                sx={{
                                  color: "shades.neutral.700",
                                }}
                              >
                                {paragraph}
                              </Typography>
                            ))}
                          </Stack>
                        )}
                      </>
                    </Stack>
                    {activeStepConfig?.next && (
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ width: "100%", py: 2, height: "78px" }}
                      >
                        <Stepper
                          activeStep={activeStep}
                          activeStepColor="shades.primary.500"
                          onClickStep={handleGoToStep}
                          steps={steps.length}
                        />
                        <Button
                          data-test-id="tutorial-next-button"
                          onClick={
                            activeStep === steps.length - 1
                              ? handleFinish
                              : handleNext
                          }
                          sx={BUTTON_STYLE}
                        >
                          {onLastStep && !showPreviewMeetingReports
                            ? "Finish"
                            : activeStepConfig.next.content}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                  {showFreeTrialFooter && (
                    <Typography
                      data-test-id="tutorial-free-trial-footer"
                      variant="bodySecondaryReg"
                      sx={{
                        height: "32px",
                        textAlign: "center",
                        py: "7px",
                        color: "shades.primary.500",
                        bgcolor: "shades.ghost.500",
                        marginTop: "auto",
                      }}
                    >
                      You’re just{" "}
                      <span style={{ fontWeight: 600 }}>
                        {stepsRemaining} {pluralize("step", stepsRemaining)}{" "}
                        away
                      </span>{" "}
                      from your free trial!
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          )}
        </Card>
      </Modal>
    </>
  );
};

const FirstTimeTutorialGate = (props: { priority: number }) => {
  const { user } = useAuth();
  const [queryParams] = useSearchParams();
  const calendarConnectReturn = queryParams.get("mm_tut_cont");

  const shouldShowPopup =
    calendarConnectReturn === "true" ||
    !user?.userMetadata?.meetingManagerTutorialComplete;

  if (!shouldShowPopup) return null;

  return <FirstTimeTutorial {...props} />;
};

export default FirstTimeTutorialGate;
